/**
 * Standalone TipTap editor used by the My Templates settings page.
 *
 * Reuses the same node schema as `PrototypeReport` — `FieldNode`,
 * `OrganSubsectionNode`, `FindingsSectionNode` — so chips render with the
 * exact same look as the case-viewer editor. Drops case-specific extensions
 * (dictation, AI-driven extensions): the rad is editing a template, not
 * authoring a report.
 *
 * Template-edit affordances on top of the read-only schema:
 *   - useSectionContextMenu: activate a section heading to Rename / Remove it,
 *     with right-click as a shortcut (same interaction as the case viewer).
 *   - Chip right-click menu: Add finding / Dismiss (see below) — the subset
 *     of PrototypeReport's ChipContextMenu that makes sense without AI or
 *     viewer context.
 *   - DragHandle for organSubsection-in-findingsSection drag-to-reorder.
 *
 * Exposes the underlying `editor` via the `onReady` callback so the parent
 * can read the doc on save and insert new sections.
 */
import { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DragHandle from "@tiptap/extension-drag-handle";

import { FieldNode } from "./tiptap/marks";
import {
  FindingsSectionNode,
  ImpressionSectionNode,
  OrganSubsectionNode,
  ImpressionItemNode,
} from "./tiptap/nodes";
import { FieldNavigation, findFieldNodeById, insertRadFinding } from "./tiptap/extensions";
import { VerticalArrowNav } from "./tiptap/verticalNav";
import { useSectionContextMenu } from "./reporter/useSectionContextMenu";
import { ChipContextMenu, type ChipAction } from "./reporter/ChipContextMenu";
import { buildReportDoc } from "../utils/reportUtils";
import type { FindingSubsectionTemplate, ReportTemplate } from "../hooks/useTemplate";
import { logger } from "../utils/logger";

interface TemplateEditorProps {
  template: ReportTemplate;
  onReady?: (editor: Editor) => void;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onReady }) => {
  const initialContent = buildReportDoc(template, {}, {}, []);
  const editor = useEditor({
    extensions: [
      StarterKit,
      FieldNode,
      FindingsSectionNode,
      ImpressionSectionNode,
      OrganSubsectionNode,
      ImpressionItemNode,
      FieldNavigation,
      VerticalArrowNav,
      DragHandle.configure({
        render: () => {
          const el = document.createElement("button");
          el.type = "button";
          el.className = "template-section-drag-handle";
          el.tabIndex = -1;
          el.setAttribute("aria-label", "Drag to reorder section");
          el.textContent = "⠿";
          return el;
        },
        nested: {
          allowedContainers: ["findingsSection"],
          rules: [
            {
              id: "organSubsectionOnly",
              evaluate: ({ node }) => (node.type.name === "organSubsection" ? -1000 : 1000),
            },
          ],
        },
        onNodeChange: ({ node }) => {
          if (!node) return;
          const dom = document.querySelector<HTMLElement>(".template-section-drag-handle");
          if (!dom) return;
          dom.dataset.targetNode = node.type.name;
        },
      }),
    ],
    content: initialContent,
    editable: true,
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none focus:outline-none min-h-[400px] p-4",
      },
      transformPastedText: (text) => text.replace(/\s*\n+\s*/g, " ").trim(),
    },
  });

  // Heading actions are shared with the case viewer.
  const sectionMenu = useSectionContextMenu(editor);

  // Right-click a chip → app context menu. Without this the browser's native
  // menu showed instead — the case viewer's ChipContextMenu never mounted
  // here. Only the two actions that make sense without AI/viewer context:
  // Add finding (empty chip in the same organ subsection) and Dismiss
  // (delete the chip). Edit attributes / Add measurement / Regenerate
  // impression all need candidate-attrs data or the case viewer, neither of
  // which this standalone editor has.
  const [chipContextMenu, setChipContextMenu] = useState<{
    chipId: string;
    anchor: { x: number; y: number };
    canDismiss: boolean;
    canAddFinding: boolean;
  } | null>(null);
  const closeChipContextMenu = useCallback(() => setChipContextMenu(null), []);

  const findChipById = useCallback(
    (chipId: string): HTMLElement | null => {
      if (!editor?.view?.dom) return null;
      return editor.view.dom.querySelector(`.report-field[data-id="${CSS.escape(chipId)}"]`);
    },
    [editor],
  );

  // Delete the chip. Resolved by data-id via findFieldNodeById, never
  // posAtDOM — the field is an atom node, so posAtDOM on its wrapper lands
  // in the parent paragraph, not the field itself (see tiptap/marks.ts).
  const dismissChip = useCallback(
    (fieldSpan: Element) => {
      if (!editor || !editor.isEditable) return;
      const chipId = fieldSpan.getAttribute("data-id");
      if (!chipId) return;
      const hit = findFieldNodeById(editor.state.doc, chipId);
      if (!hit) return;
      const tr = editor.state.tr;
      let end = hit.pos + hit.node.nodeSize;
      // Swallow a trailing space separator so chip spacing stays clean.
      const after = tr.doc.textBetween(end, Math.min(end + 1, tr.doc.content.size));
      if (after === " ") end += 1;
      tr.delete(hit.pos, end);
      editor.view.dispatch(tr);
      editor.commands.focus();
    },
    [editor],
  );

  // Append an empty chip to the chip's organ subsection and focus it — the
  // template-editor counterpart of PrototypeReport's chip right-click "Add
  // finding". Same insertRadFinding helper; no reportData/AI dependency.
  const handleAddFinding = useCallback(
    (fieldSpan: Element) => {
      if (!editor || !editor.isEditable) return;
      const kind = fieldSpan.closest(".organ-subsection")?.getAttribute("data-kind");
      if (!kind) return;
      // Position just inside the close of the section's first paragraph.
      const paraEnds: number[] = [];
      editor.state.doc.descendants((node, pos) => {
        if (paraEnds.length) return false;
        if (node.type.name === "organSubsection" && node.attrs.kind === kind) {
          node.forEach((child, off) => {
            if (paraEnds.length || child.type.name !== "paragraph") return;
            paraEnds.push(pos + 1 + off + child.nodeSize - 1);
          });
          return false;
        }
        return true;
      });
      const paraInnerEnd = paraEnds[0];
      if (paraInnerEnd !== undefined) insertRadFinding(editor.view, paraInnerEnd);
    },
    [editor],
  );

  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    const onContextMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const chip = target.closest(".report-field") as HTMLElement | null;
      if (!chip || !editor.isEditable) return;
      event.preventDefault();
      event.stopPropagation();
      const chipId = chip.getAttribute("data-id");
      if (!chipId) return;
      setChipContextMenu({
        chipId,
        anchor: { x: event.clientX, y: event.clientY },
        canDismiss: editor.isEditable,
        canAddFinding: !!chip.closest(".organ-subsection"),
      });
    };
    dom.addEventListener("contextmenu", onContextMenu);
    return () => dom.removeEventListener("contextmenu", onContextMenu);
  }, [editor]);

  // Dispatcher for the chip context menu — looks up the chip by id (DOM may
  // have re-rendered between menu open and action) and runs the
  // corresponding helper. canEditAttrs/canMeasure/canRegenerate are always
  // false here, so ChipContextMenu never renders those items and onAction
  // never receives them in practice; the else branch is a loud guard against
  // that assumption silently breaking if a future prop change adds one back
  // without wiring the branch here.
  const handleChipMenuAction = useCallback(
    (action: ChipAction) => {
      if (!chipContextMenu) return;
      const chip = findChipById(chipContextMenu.chipId);
      if (!chip) {
        logger.warn(
          "[TemplateEditor] chip menu action skipped: chip no longer in the doc",
          chipContextMenu.chipId,
        );
        return;
      }
      if (action === "add-finding") handleAddFinding(chip);
      else if (action === "dismiss") dismissChip(chip);
      else logger.warn(`[TemplateEditor] chip menu action not wired: ${action}`);
    },
    [chipContextMenu, findChipById, handleAddFinding, dismissChip],
  );

  useEffect(() => {
    if (editor && onReady) {
      onReady(editor);
    }
  }, [editor, onReady]);

  const templateId = template.id;
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(buildReportDoc(template, {}, {}, []));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, templateId]);

  return (
    <div className="report-container border border-border-strong rounded-lg bg-bg-2">
      <EditorContent editor={editor} className="report-editor" />
      {sectionMenu}
      {chipContextMenu && (
        <ChipContextMenu
          anchor={chipContextMenu.anchor}
          canEditAttrs={false}
          canMeasure={false}
          canDismiss={chipContextMenu.canDismiss}
          canAddFinding={chipContextMenu.canAddFinding}
          canRegenerate={false}
          onAction={handleChipMenuAction}
          onClose={closeChipContextMenu}
        />
      )}
    </div>
  );
};

/**
 * Walk the editor's doc and emit the rad's complete section structure:
 * one entry per `organSubsection` node, in document order, capturing the
 * current kind, label, and joined chip text as `default`.
 *
 * Removed sections (deleted through the heading menu) are absent from
 * the walk, so they're absent from the result — that's how the deletion
 * persists to the BE.
 */
export function extractTemplateStructure(editor: Editor): FindingSubsectionTemplate[] {
  const sections: FindingSubsectionTemplate[] = [];
  editor.state.doc.descendants((node) => {
    if (node.type.name !== "organSubsection") return;
    const kind = (node.attrs.kind as string | undefined) ?? "";
    const label = (node.attrs.label as string | undefined) ?? "";
    if (!kind) return;
    const chipTexts: string[] = [];
    node.descendants((inner) => {
      if (inner.type.name === "field") {
        chipTexts.push((inner.attrs.text as string | undefined) ?? "");
        return false;
      }
      return undefined;
    });
    sections.push({
      kind,
      label,
      default: chipTexts.join(" ").trim(),
    });
  });
  return sections;
}
